import { useEffect, useRef, useState } from 'react';
import {
    Button,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useRequest } from '../api/Request';
import { Question } from '../database/theSource';
import { AuthStatus, useAuth } from '../auth/Auth';
import QuestionSubmissionModal from './QuestionSubmissionModal';
import { ArtistInfo, User } from '../database/user';
import { ResponseType } from './constants/ResponseType';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaymentModel } from './constants/PaymentModel';

interface QuestionSubmissionFormProps {
    artist?: User;
    onSubmit: (question: Question) => void;
}

const QuestionSubmissionForm: React.FC<QuestionSubmissionFormProps> = ({
    artist,
    onSubmit,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [responseType, setResponseType] = useState<string>(
        searchParams.get('responseType') || ResponseType.Text
    );
    const [isPrivate, setIsPrivate] = useState<string>(
        searchParams.get('isPrivate') || 'false'
    );
    const [isAcknowledged, setIsAcknowledged] = useState<boolean>(
        searchParams.get('acknowledgement') === 'true' ? true : false
    );
    const [questionContent, setQuestionContent] = useState(
        searchParams.get('questionContent') || ''
    );
    const [selectedCost, setSelectedCost] = useState<number>(
        searchParams.get('selectedCost')
            ? parseFloat(searchParams.get('selectedCost') as string)
            : 0
    );

    const [helperText, setHelperText] = useState<string>('150 character limit');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(
        searchParams.toString().trim().length > 0
    );
    const textFieldRef = useRef<HTMLInputElement>(null);
    const getArtistUserRequest = useRequest<User>();
    const theme = useTheme();
    const auth = useAuth();
    const navigate = useNavigate();
    const user = auth.user;
    const formStackDirectionIsCol = useMediaQuery('(max-width:500px)');

    useEffect(() => {
        if (textFieldRef.current) {
            textFieldRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (!searchParams.get('selectedCost') && artist?.artistInfo?.pwywPrice) {
            if (responseType === ResponseType.Text) {
                setSelectedCost(artist.artistInfo.pwywPrice.text.suggested || 0);
            } else if (responseType === ResponseType.Video) {
                setSelectedCost(artist.artistInfo.pwywPrice.video.suggested || 0);
            }
        }
    }, [responseType, artist?.artistInfo?.pwywPrice, searchParams]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setSearchParams();
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSearchParams();
    };

    const handleResponseTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const type = event.target.value as ResponseType;
        setResponseType(type);
    };

    const handlePrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isPrivate = event.target.value;
        setIsPrivate(isPrivate);
    };

    function isTheSourceSetUp(artist?: User): boolean {
        if (
            !artist ||
            !artist.artistInfo ||
            !artist.artistInfo.stripeAccountId ||
            !artist.artistInfo.onboardingComplete
        ) {
            return false;
        }

        return isPaymentModelValid(artist.artistInfo);
    }

    function isPaymentModelValid(artistInfo: ArtistInfo): boolean {
        switch (artistInfo.paymentModel) {
            case PaymentModel.STANDARD:
                if (
                    !artistInfo.costTextResponseType ||
                    artistInfo.costTextResponseType < 1 ||
                    !artistInfo.costVideoResponseType ||
                    artistInfo.costVideoResponseType < 1
                ) {
                    return false;
                }
                break;

            case PaymentModel.PWYW:
                if (!artistInfo.pwywPrice) {
                    return false;
                }

                if (
                    !artistInfo.pwywPrice.text.min ||
                    !artistInfo.pwywPrice.video.min ||
                    !artistInfo.pwywPrice.text.suggested ||
                    !artistInfo.pwywPrice.video.suggested ||
                    !artistInfo.pwywPrice.text.max ||
                    !artistInfo.pwywPrice.video.max
                ) {
                    return false;
                }

                if (artistInfo.pwywPrice.text.min < 1) {
                    return false;
                }

                if (artistInfo.pwywPrice.video.min < 1) {
                    return false;
                }

                if (
                    artistInfo.pwywPrice.text.suggested >=
                        artistInfo.pwywPrice.text.max ||
                    artistInfo.pwywPrice.text.suggested <= artistInfo.pwywPrice.text.min
                ) {
                    return false;
                }

                if (
                    artistInfo.pwywPrice.video.suggested >=
                        artistInfo.pwywPrice.video.max ||
                    artistInfo.pwywPrice.video.suggested <= artistInfo.pwywPrice.video.min
                ) {
                    return false;
                }

                if (artistInfo.pwywPrice.text.max <= artistInfo.pwywPrice.text.min) {
                    return false;
                }

                if (artistInfo.pwywPrice.video.max <= artistInfo.pwywPrice.video.min) {
                    return false;
                }
                break;

            default:
                return false; // Should not default to any other model
        }

        return true;
    }

    if (getArtistUserRequest.isLoading() || auth.status === AuthStatus.Loading) {
        return (
            <Stack justifyContent='center' alignItems='center' padding={10}>
                <CircularProgress />
            </Stack>
        );
    } else if (!user) {
        return (
            <Stack
                direction='column'
                spacing={4}
                alignItems='center'
                justifyContent='center'
            >
                <Tooltip
                    title={`You must have an account to talk to ${artist?.displayName}`}
                >
                    <Typography variant='h5' textAlign='center'>
                        Want to talk to {artist?.displayName} directly?
                    </Typography>
                </Tooltip>
                <Stack
                    direction='row'
                    justifyContent='center'
                    alignItems='center'
                    spacing={4}
                >
                    <Button variant='contained' onClick={() => navigate('/signin')}>
                        Sign In
                    </Button>
                    <Divider>or</Divider>
                    <Button variant='contained' onClick={() => navigate('/signup')}>
                        Sign Up
                    </Button>
                </Stack>
            </Stack>
        );
    } else if (!artist || !artist.artistInfo || !isTheSourceSetUp(artist)) {
        return (
            <Container
                maxWidth='xl'
                sx={{
                    mt: '50px',
                }}
            >
                <Stack alignItems='center'>
                    <Typography variant='h6'>
                        This feature has not been set up.
                    </Typography>
                </Stack>
            </Container>
        );
    } else {
        return (
            <Container
                maxWidth='xl'
                sx={{
                    mt: '50px',
                    pb: 3,
                    px: { xs: 2, sm: 3, md: 4 },
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(139, 0, 0, 0.3)',
                    border: `1px solid ${theme.palette.primary.main}`,
                }}
            >
                <Stack spacing={3}>
                    <Typography
                        variant='h5'
                        fontFamily='LemonMilk'
                        textAlign='center'
                        sx={{ pt: 2 }}
                    >
                        Send a Request to {artist?.displayName}
                    </Typography>

                    <TextField
                        label={`Your Request`}
                        placeholder={`Ask a question, make a request, whatever you like!`}
                        value={questionContent}
                        onChange={(e) => {
                            setHelperText(
                                `${150 - e.target.value.length} characters remaining`
                            );
                            setQuestionContent(e.target.value);
                        }}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        inputProps={{ maxLength: 150 }}
                        helperText={helperText}
                        inputRef={textFieldRef}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                            },
                        }}
                    />

                    <Stack
                        direction={formStackDirectionIsCol ? 'column' : 'row'}
                        spacing={3}
                        alignItems={formStackDirectionIsCol ? 'stretch' : 'center'}
                        justifyContent='space-between'
                        sx={{
                            p: 2,
                            borderRadius: '12px',
                        }}
                    >
                        <Stack
                            direction='row'
                            spacing={formStackDirectionIsCol ? 2 : 4}
                            sx={{ flex: 1 }}
                        >
                            <FormControl>
                                <FormLabel
                                    sx={{
                                        color: `${theme.palette.primary.main}`,
                                        fontFamily: 'LemonMilk',
                                        fontWeight: 'bold',
                                        mb: 1,
                                    }}
                                >
                                    Response Type
                                </FormLabel>
                                <RadioGroup
                                    value={responseType}
                                    onChange={handleResponseTypeChange}
                                    row={!formStackDirectionIsCol}
                                >
                                    <FormControlLabel
                                        value={ResponseType.Text}
                                        control={<Radio />}
                                        label={ResponseType.Text}
                                    />
                                    <FormControlLabel
                                        value={ResponseType.Video}
                                        control={<Radio />}
                                        label={ResponseType.Video}
                                    />
                                </RadioGroup>
                            </FormControl>

                            <Divider
                                orientation={
                                    formStackDirectionIsCol ? 'horizontal' : 'vertical'
                                }
                                flexItem
                                sx={{ opacity: 0.3 }}
                            />

                            <FormControl>
                                <FormLabel
                                    sx={{
                                        color: `${theme.palette.primary.main}`,
                                        fontFamily: 'LemonMilk',
                                        fontWeight: 'bold',
                                        mb: 1,
                                    }}
                                >
                                    Privacy
                                </FormLabel>
                                <RadioGroup
                                    value={isPrivate}
                                    onChange={handlePrivacyChange}
                                    row={!formStackDirectionIsCol}
                                >
                                    <FormControlLabel
                                        value={'false'}
                                        control={<Radio />}
                                        label={'Public'}
                                    />
                                    <FormControlLabel
                                        value={'true'}
                                        control={<Radio />}
                                        label={'Private'}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>

                        <Button
                            variant='contained'
                            onClick={handleOpenModal}
                            disabled={questionContent.trim().length === 0}
                            size='large'
                            fullWidth={formStackDirectionIsCol}
                            sx={{
                                minWidth: formStackDirectionIsCol ? '100%' : '160px',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                padding: '14px 28px',
                                borderRadius: '12px',
                                boxShadow:
                                    questionContent.trim().length > 0
                                        ? '0px 6px 20px rgba(139, 0, 0, 0.4)'
                                        : 'none',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform:
                                        questionContent.trim().length > 0
                                            ? 'translateY(-2px)'
                                            : 'none',
                                    boxShadow:
                                        questionContent.trim().length > 0
                                            ? '0px 8px 24px rgba(139, 0, 0, 0.5)'
                                            : 'none',
                                },
                                '&:disabled': {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            Submit Request
                        </Button>
                    </Stack>
                </Stack>
                <QuestionSubmissionModal
                    isModalOpen={isModalOpen}
                    handleCloseModal={handleCloseModal}
                    onSubmit={(question) => {
                        setQuestionContent('');
                        setResponseType(ResponseType.Text);
                        setIsPrivate('false');
                        onSubmit(question);
                        setHelperText('150 character limit');
                    }}
                    user={user}
                    artistInfo={artist.artistInfo}
                    questionContent={questionContent}
                    responseType={responseType}
                    isAcknowledged={isAcknowledged}
                    isPrivate={isPrivate}
                    setIsAcknowledged={setIsAcknowledged}
                    selectedCost={selectedCost}
                    setSelectedCost={setSelectedCost}
                />
            </Container>
        );
    }
};

export default QuestionSubmissionForm;
